/* eslint-disable no-unused-vars */
import { ArrowBack, ArrowForward, Save } from '@mui/icons-material'
import { Box, CircularProgress, Divider, useMediaQuery } from '@mui/material'
import { IconDeviceFloppy, IconPencil, IconX } from '@tabler/icons-react'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { ContractFlowActionDropdown, PropsWithWizard } from '~/components'
import { Button } from '~/components/Form/Button'
import { useContract } from '~/contexts'
import { EContractStatus } from '~/graphql/types'
import { usePermission } from '~/hooks'
import { theme } from '~/utils'

type ActionButtonsProps = {
  loading: boolean
  onSubmit: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>
}
export const ActionButtons: React.FC<PropsWithWizard<ActionButtonsProps>> = (props) => {
  const { onNext, onPrev, loading, onSubmit } = props
  const { contract, isDisabledContractUpdate, setIsDisabledContractUpdate, setIsUpdatingContract, isUpdatingContract } = useContract()
  const { formState, trigger } = useFormContext()
  const canCreateEditContracts = usePermission(['contracts.updated', 'contracts.create'])

  const isLowerMd = useMediaQuery(theme.breakpoints.down('md'))
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))

  const isDeactivated = contract?.status === EContractStatus.deactivated
  const isDeactivating = contract?.status === EContractStatus.inDeactivation
  const isCancelling = contract?.status === EContractStatus.cancellationRequested

  const shouldDisableButtons = useMemo(() => isDeactivated || isDeactivating || isCancelling || loading || !canCreateEditContracts, [isDeactivated, isDeactivating, isCancelling, loading, canCreateEditContracts])

  const handleSubmit = async (submitType: 'continue' | 'save') => {
    const getErrorEntries = (obj: any, prefix = ''): { field: string, message: string }[] => {
      const entries: { field: string, message: string }[] = []
      Object.entries(obj || {}).forEach(([key, val]) => {
        const path = prefix ? `${prefix}.${key}` : key
        if (val && typeof val === 'object') {
          const message = (val as any).message
          if (message) entries.push({ field: path, message })
          entries.push(...getErrorEntries(val as any, path))
        }
      })
      return entries
    }

    const isValid = await trigger()

    if (!isValid) {
      const errors = getErrorEntries(formState.errors)
      console.table(errors.length ? errors : [{ field: '-', message: 'Form inválido' }])
    }

    await onSubmit()

    if (isValid && submitType === 'continue') {
      onNext()
    }
  }

  return (
    <>
      <Divider sx={{ marginTop: 1, marginBottom: 1 }} />

      <Box sx={{ flexDirection: 'row', justifyContent: 'space-between', display: 'flex' }} >
        {!isLowerMd && (
          <Button disabled={loading} startIcon={<ArrowBack />} color='secondary' onClick={onPrev}>
            Voltar
          </Button>
        )}

        <Box sx={{ display: 'flex', flexDirection: isLowerSm ? 'column' : 'row', gap: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: isLowerSm ? 'space-between' : 'flex-end' }}>
            <ContractFlowActionDropdown disabled={shouldDisableButtons} />
            {isDisabledContractUpdate ? (
              <Button
                onClick={() => (setIsDisabledContractUpdate(false), setIsUpdatingContract(true))}
                size='small'
                color='secondary'
                startIcon={<IconPencil />}>
                Editar dados
              </Button>
            )
              : (
                <>
                  {isUpdatingContract ? (
                    <>
                      <Button
                        onClick={() => (setIsDisabledContractUpdate(true), setIsUpdatingContract(false))}
                        startIcon={<IconX />}
                        color='secondary'
                        size='small'
                        disabled={loading}
                      >
                        Cancelar edição
                      </Button>
                      <Button
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={24} color='inherit' /> : <IconDeviceFloppy />}
                        onClick={() => handleSubmit('save')}
                      >
                        Gravar dados
                      </Button>
                    </>
                  )
                    : (
                      <>
                        <Button
                          onClick={() => handleSubmit('save')}
                          disabled={shouldDisableButtons}
                          startIcon={loading ? <CircularProgress size={24} color='inherit' /> : <Save />}
                          color='secondary'
                          size='small'
                        >
                          Gravar dados
                        </Button>
                        <Button
                          disabled={shouldDisableButtons}
                          endIcon={<ArrowForward />}
                          onClick={() => handleSubmit('continue')}
                        >
                          Continuar
                        </Button>
                      </>
                    )}
                </>
              )}
          </Box>
        </Box>
      </Box>
    </>
  )
}
